import { useState, useRef, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { Subscription } from 'rxjs';
import { useObservable } from '@/shared/hooks/useObservable';
import { notificationService } from '../services/notification.service';
import { mapService } from '@/features/map/services/map.service';
import { authService } from '@/features/auth/services/auth.service';
import { flightCategoriesService } from '../services/flightCategories.service';
import { Button } from '@/shared/components/Button';
import type { GenerateResponse } from '@/shared/types';
import {formatDrfError} from "@/shared/api/http.ts";

function PreviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between items-baseline py-1.5 border-b border-slate-800 last:border-0">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="text-xs text-slate-300 text-right max-w-[55%]">{value}</span>
    </div>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return <div className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">{children}</div>;
}

export function StepPreview() {
  const data = useObservable(notificationService.formData$, notificationService.formData$.value);
  const geometry = useObservable(mapService.geometry$, mapService.geometry$.value);
  const user = useObservable(authService.user$, null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GenerateResponse | null>(null);
  const [wasSent, setWasSent] = useState(false);
  const subRef = useRef<Subscription | null>(null);

  useEffect(() => () => { subRef.current?.unsubscribe(); }, []);

  const operator = user?.operators.find(o => o.id === data.operatorId);
  const uas = user?.uasList.find(u => u.id === data.uasId);

  function handleGenerateAndSend() {
    subRef.current?.unsubscribe();
    setError(null);
    setLoading(true);
    subRef.current = notificationService.submit(geometry).subscribe({
      next: res => { setResult(res); setWasSent(true); setLoading(false); },
      error: (err) => {
        setError(formatDrfError(err, 'Napaka pri generiranju / Generation failed. Please retry.'));
        setLoading(false);
      },
    });
  }

  function handleGenerateOnly() {
    subRef.current?.unsubscribe();
    setError(null);
    setLoading(true);
    subRef.current = notificationService.generateOnly(geometry).subscribe({
      next: res => { setResult(res); setWasSent(false); setLoading(false); },
      error: (err) => {
        setError(formatDrfError(err, 'Napaka pri generiranju / Generation failed. Please retry.'));
        setLoading(false);
      },
    });
  }

  return (
    <div className="space-y-4">
      <SectionTitle>Pregled / Preview</SectionTitle>

      <div className="bg-slate-900 border border-slate-700 rounded-md p-3">
        <PreviewRow label="Operator" value={operator?.name ?? data.operatorId} />
        <PreviewRow label="UAS" value={uas?.type ?? data.uasId} />
        <PreviewRow label="Pilot tel." value={data.pilotPhone} />
        <PreviewRow
          label="Kategorija"
          value={flightCategoriesService.getById(data.category)?.name ?? data.category}
        />
        <PreviewRow label="Datum / Date" value={data.flightDate} />
        <PreviewRow label="Čas / Time" value={`${data.flightTimeFrom} – ${data.flightTimeTo}`} />
        <PreviewRow label="Lokacija" value={data.location} />
        <PreviewRow label="Višina / Altitude" value={`${data.plannedAltitude} m AGL`} />
        {geometry.takeoffPoint && (
          <PreviewRow
            label="Vzlet / Takeoff"
            value={`${geometry.takeoffPoint.lat.toFixed(5)}°N, ${geometry.takeoffPoint.lng.toFixed(5)}°E`}
          />
        )}
        {geometry.additionalTakeoffPoints.length > 0 && (
          <PreviewRow
            label="Dodatne točke vzleta"
            value={`${geometry.additionalTakeoffPoints.length} točk / points`}
          />
        )}
      </div>

      <SectionTitle>Pošlji & prenesi / Send & download</SectionTitle>

      <div className="bg-slate-900 border border-slate-700 rounded-md p-3 text-xs text-slate-500 leading-relaxed">
        E-pošta bo poslana na: <span className="text-blue-400">uav@caa.si</span>
        <br />
        <span className="text-slate-600">Email will be sent to: uav@caa.si</span>
      </div>

      {error && <p className="text-rose-400 text-xs">{error}</p>}

      {!result ? (
        <div className="space-y-2">
          <Button variant="success" disabled={loading} onClick={handleGenerateAndSend} className="w-full">
            {loading ? 'Generiranje...' : '✉ Generiraj & pošlji / Generate & send'}
          </Button>
          <Button variant="secondary" disabled={loading} onClick={handleGenerateOnly} className="w-full">
            {loading ? 'Generiranje...' : '📄 Generiraj / Generate'}
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-emerald-400 text-xs">
            {wasSent ? '✓ Poslano / Sent successfully' : '✓ Generirano / Generated (not sent)'}
          </p>
          <a
            href={result.pdfUrl}
            download="UAS_najava_leta.pdf"
            className="flex items-center gap-3 bg-slate-900 border border-slate-700 rounded-md px-3 py-2.5 text-xs text-slate-300 hover:border-slate-500 transition-colors"
          >
            <span>📄</span>
            <span className="flex-1">UAS_najava_leta.pdf</span>
            <span className="text-slate-500">↓ PDF</span>
          </a>
          <a
            href={result.kmlUrl}
            download="UAS_obmocje_letenja.kml"
            className="flex items-center gap-3 bg-slate-900 border border-slate-700 rounded-md px-3 py-2.5 text-xs text-slate-300 hover:border-slate-500 transition-colors"
          >
            <span>🗺️</span>
            <span className="flex-1">UAS_obmocje_letenja.kml</span>
            <span className="text-slate-500">↓ KML</span>
          </a>
        </div>
      )}
    </div>
  );
}
